import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';
import * as fromAction from './booking.action';
import { Action } from '@ngrx/store';
import { map, retry, switchMap, tap } from 'rxjs/operators';
import { BookingApiService } from '../client/services/booking.api.service';
import { BookingStoreService } from './booking.store.service';
import { Router } from '@angular/router';
import { BookingFilter } from '../models/booking.filter';
import { IdentityApiService } from "@modules/identity/store/services/identity-api.service";
import { bookingsFeatureKey } from './booking.state';
import { EffectHelperService } from '@modules/core/services/effect-helper.service';
import { ClassroomCourseApiService } from '@modules/course/classroom/store/services/classroom-course.api.service';
import { InstructorApiService } from '@modules/instructor/client/services/instructor.api.service';
import { DigitalCourseApiService } from '@modules/course/digitalcourse/store/services';
import { AlertMessageType } from '@shared/enums/alert-message-type';
import { MatDialog } from '@angular/material/dialog';

@Injectable()
export class BookingStoreEffects {

  fetchBookingEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.FetchBookings),
      switchMap((payload) =>
        this.bookingApiService
          .getAllBookings(payload.search, payload.filter, payload.pageIndex, payload.pageSize, payload.sort)
          .pipe(
            retry(1),
            map((res) => {
              this.bookingStoreService.updatePageIndex();
              this.bookingStoreService.addTotalCount(res.total);
              return this.effectHelperService.handleSuccess(fromAction.AddBookings({ bookings: res.items }))
            }),
            this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
          )
      )
    )
  );

  fetchMoreBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.FetchMoreBookings),
    concatLatestFrom(() => this.bookingStoreService.getPageIndex()),
    switchMap(([payload, pageIndex]) =>
      this.bookingApiService
        .getAllBookings(payload.search, payload.filter, pageIndex, payload.pageSize, payload.sort)
        .pipe(
          retry(1),
          map((res) => {
            this.bookingStoreService.updatePageIndex();
            return this.effectHelperService.handleSuccess(fromAction.AddMoreBookings({ bookings: res.items }))
          }),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        )
    )
  ));

  fetchSingleBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.FetchSingleBookings),
    switchMap((payload) =>
      this.bookingApiService
        .getAllSingleBookings(payload.search, payload.filter, payload.pageIndex, payload.pageSize, payload.sort)
        .pipe(
          map((booking) => this.effectHelperService.handleSuccess(fromAction.AddBooking({ booking })),
            this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        ))
    )
  )
  );

  getByIdEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.GetBooking),
    switchMap((payload) =>
      this.bookingApiService.getById(payload.id).pipe(
        map((booking) =>
          this.effectHelperService.handleSuccess(fromAction.AddBooking({ booking })),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        ))
    )
  )
  );

  saveBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.SaveBooking),
    switchMap((payload) => {
      return this.bookingApiService
        .bookingExperience(payload.booking)
        .pipe(map((booking) => this.effectHelperService.handleSuccess(fromAction.AddBooking({ booking })),
          this.effectHelperService.handleError(fromAction.AddError, false, bookingsFeatureKey)
        ))
    })
  )
  );

  updateBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.EditBooking),
    switchMap((payload) =>
      this.bookingApiService
        .updateBooking(payload.id, payload.change)
        .pipe(
          map((booking) => {
            this.bookingStoreService.updateBookingSuccess();
            return this.effectHelperService.handleSuccess(fromAction.UpdateBooking({ id: payload.id, change: booking }))
          }),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        ))
  )
  );

  approveBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.ApproveBooking),
    switchMap((payload) =>
      this.bookingApiService
        .approveBooking(payload.id, payload.change)
        .pipe(
          map((booking) => {
            this.bookingStoreService.updateBookingSuccess();
            return this.effectHelperService.handleSuccess(fromAction.UpdateBooking({ id: payload.id, change: booking }))
          }),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        ))
  )

  );


  cancelBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.CancelBooking),
    switchMap((payload) =>
      this.bookingApiService
        .cancelBooking(payload.id, payload.change)
        .pipe(
          map((booking) => this.effectHelperService.handleSuccess(fromAction.DeleteBooking({ id: payload.id })),
            this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
          ))
    )
  )
  );

  rejectBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.RejectBooking),
    switchMap((payload) =>
      this.bookingApiService
        .rejectBooking(payload.id, payload.change)
        .pipe(
          map((booking) => this.effectHelperService.handleSuccess(fromAction.DeleteBooking({ id: payload.id })),
            this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
          ))
    )
  )
  );


  paidBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.PaidBooking),
    switchMap((payload) =>
      this.bookingApiService
        .approveBooking(payload.id, payload.change)
        .pipe(
          map((booking) => this.effectHelperService.handleSuccess(fromAction.UpdateBooking({ id: payload.id, change: booking })),
            this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
          ))
    )
  )
  );

  registerBookingEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.RegisterBooking),
      switchMap((payload) =>
        this.bookingApiService.registerBooking(payload.change).pipe(
          map((booking) => {
            if (
              booking.serviceRef.bookingType === 'Course' ||
              booking.serviceRef.bookingType === 'Classroom'
            ) {
              if (booking.price.pricePerPerson > 0) {
                this.router.navigate([`/payment/payment`, booking.id]);
                if (booking.serviceRef.bookingType === 'Classroom') {
                  this.router.navigate(['/payment/checkout', booking.id], { replaceUrl: true });
                }
              } else {
                this.router.navigate(['/payment/thankyou', booking.id]);
              }
            } else {
              this.router.navigate(['/booking/mine-bookinger/kurs/aktiv']);
            }

            return this.effectHelperService.handleSuccess(fromAction.AddBooking({ booking }))
          }),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        )
      ),
    )
  );


  registerMultiBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.RegisterMultiBooking),
    switchMap((payload) =>
      this.bookingApiService
        .registerMultiBooking(payload.bookings)
        .pipe(
          map((bookings) => {
            let bookingIds = bookings.map((booking) => booking.id);
            const participants = bookingIds.length;
            this.router.navigate([`/payment/checkout/${bookingIds.shift()}`], {
              queryParams: { ids: bookingIds, participants: participants },
            });
            return this.effectHelperService.handleSuccess(fromAction.AddBookings({ bookings }))
          }),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        ))
  ),
  );


  registerServiceBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.RegisterServiceBooking),
    switchMap((payload) =>
      this.bookingApiService
        .registerBooking(payload.change)
        .pipe(
          map((booking) =>
            this.effectHelperService.handleSuccess(fromAction.AddServiceBooking({ booking, isInstantBooking: payload.isInstantBooking }))),
            this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
          )
    ),
  )
  );

  addServiceBookingEffect$: Observable<unknown> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.AddServiceBooking),
    tap(({ booking, isInstantBooking }) => {
      if (isInstantBooking) {
        this.router.navigate(['/payment/checkout', booking.id]);
      }
      else {
        this.router.navigate(['/booking/mine-bookinger', 'tjeneste', 'pending'])
      }
    })
  ), {
    dispatch: false
  }
  );

  saveServiceBookingEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.SaveServiceBooking),
      switchMap((payload) =>
        this.bookingApiService
          .bookingExperience(payload.booking)
          .pipe(
            map((booking) =>
              this.effectHelperService.handleSuccess(fromAction.AddServiceBooking({ booking, isInstantBooking: false }))),
              this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
            )
      )
    )
  );

  approveServiceBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.ApproveServiceBooking),
    switchMap((payload) =>
      this.bookingApiService
        .approveBooking(payload.id, payload.change)
        .pipe(
          map((booking) => {
            this.bookingStoreService.approveServiceBookingSuccess()
            return this.effectHelperService.handleSuccess(fromAction.UpdateServiceBooking({ id: payload.id, change: booking }))
          }),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        )
    )
  )
  );


  cancelServiceBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.CancelServiceBooking),
    switchMap((payload) =>
      this.bookingApiService
        .cancelBooking(payload.id, payload.change)
        .pipe(
          map((booking) => this.effectHelperService.handleSuccess(fromAction.DeleteServiceBooking({ id: payload.id }))),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        ))
  )
  )

  rejectServiceBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.RejectServiceBooking),
    switchMap((payload) =>
      this.bookingApiService
        .rejectBooking(payload.id, payload.change)
        .pipe(
          map((booking) => this.effectHelperService.handleSuccess(fromAction.DeleteServiceBooking({ id: payload.id }))),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        ))
  )
  );

  checkBookingEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.CheckBooking),
    switchMap((payload) =>
      this.bookingApiService
        .checkBooking(payload.userId, payload.courseId)
        .pipe(
          map((isCourseBooked) => this.effectHelperService.handleSuccess(fromAction.AddIsCourseBooked({ isCourseBooked }))),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        ))
  )
  );

  acceptRescheduleEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.AcceptBookingReschedule),
      switchMap((payload) =>
        this.bookingApiService
          .acceptReschedule(payload.id)
          .pipe(
            map((booking) => this.effectHelperService.handleSuccess(fromAction.UpdateBookingReschedule({ booking }))),
            this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
          ))
    )
  );

  refundBookingEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.RefundBooking),
      switchMap((payload) =>
        this.bookingApiService.refundBooking(payload.bookingId).pipe(
          switchMap((booking) => {
            const filter = new BookingFilter();
            filter.includeActive = true;
            filter.status = 'Paid';
            filter.userId = payload.userId;
            return this.bookingApiService.getAllBookings('', filter).pipe(
              map((list) => this.effectHelperService.handleSuccess(fromAction.AddBookings({ bookings: list.items }))),
              this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
            );
          })
        )
      )
    )
  );

  activateDigitalCourse$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.ActivateDigitalCourseBookingInit),
      switchMap(({ bookingId, userId }) => {
        return this.identityApiService.createMoodleUser(userId, bookingId).pipe(
          map((userInfo: unknown) => this.effectHelperService.handleSuccess(fromAction.ActivateDigitalCourseBookingSuccess({ bookingId }))),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        );
      })
    )
  );

  submitClassroomCourseReview: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.SubmitClassroomCourseReview),
      switchMap(({ serviceId, review }) => {
        return this.classroomCourseApiService.sendCourseReview(serviceId, review).pipe(
          map(() => {
            this.matDialog.closeAll();
            return this.effectHelperService.handleSuccess(fromAction.UpdateBookingReviewState({serviceId}), true, 'thanks_for_your_feedback', bookingsFeatureKey, AlertMessageType.SUCCESSFUL);
          }),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        );
      })
    )
  );

  submitDigitalCourseReview: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.SubmitDigitalCourseReview),
      switchMap(({ serviceId, review }) => {
        return this.digitalCourseApiService.sendCourseReview(serviceId, review).pipe(
          map(() => {
            this.matDialog.closeAll();
            return this.effectHelperService.handleSuccess(fromAction.UpdateBookingReviewState({serviceId}), true, 'thanks_for_your_feedback', bookingsFeatureKey, AlertMessageType.SUCCESSFUL);
          }),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        );
      })
    )
  );

  submitClassroomFeedback: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.SubmitClassroomFeedback),
      switchMap(({ serviceId, feedback }) => {
        return this.classroomCourseApiService.sendCourseFeedback(serviceId, feedback).pipe(
          map(() => null),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        );
      })
    ),{
          dispatch: false
      }
  );

  submitInstructorReview: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.SubmitInstructorReview),
      switchMap(({ instructorId, instructorReview }) => {
        return this.instructorApiService.sendInstructorReview(instructorId, instructorReview).pipe(
          map((review) => null),
          this.effectHelperService.handleError(fromAction.AddError, true, bookingsFeatureKey)
        );
      })
    ),{
          dispatch: false
      }
  );

  constructor(
    private bookingApiService: BookingApiService,
    private classroomCourseApiService: ClassroomCourseApiService,
    private digitalCourseApiService: DigitalCourseApiService,
    private instructorApiService: InstructorApiService,
    private identityApiService: IdentityApiService,
    private actions$: Actions,
    private bookingStoreService: BookingStoreService,
    private effectHelperService: EffectHelperService,
    private matDialog: MatDialog,
    private router: Router
  ) { }
}
